import { useContext } from 'react';
import {
  useSelectedWorkspace,
  useToken,
} from '../../Flow/ConsentContextProvider';
import { ReactMcpContext } from './McpContextProvider';
import {
  MCP_RESUME_FORM_FIELDS,
  MCP_SELECTED_TOOLSETS_SEPARATOR,
} from './domain/contract';
import { buildMcpSelectedScopesForPost } from './domain/scopeSelection';
import { parseSelectedSiteUrls } from './domain/availableSites';

const McpFlowParameters = () => {
  const mcpContext = useContext(ReactMcpContext);
  const { selectedWorkspace } = useSelectedWorkspace();
  const token = useToken();

  // Agent MCP: the server grants every requested scope when selectedScopes is
  // absent and derives the site from the signed Agent ARI, so post nothing.
  if (mcpContext?.agentMcpResource) {
    return null;
  }

  const selectedNames =
    mcpContext?.mcpSelectedToolsetNames ?? new Set<string>();
  const byIntent = mcpContext?.mcpByIntent ?? [];
  const allToolsets = byIntent.flatMap((g) => g.toolsets);

  // Only post names that are still in the effective toolset list
  const allNames = new Set(allToolsets.map((t) => t.name));
  const postedNames = [...selectedNames].filter((name) => allNames.has(name));

  const selectedScopesJoined = buildMcpSelectedScopesForPost(
    postedNames,
    allToolsets,
    token.scopes,
  ).join(' ');

  const selectedToolsetsJoined = postedNames.join(
    MCP_SELECTED_TOOLSETS_SEPARATOR,
  );
  // Only submit selections whose policy check completed successfully, and only
  // for sources that speak for this flow. Accept is disabled on any error, and
  // a source outside getOrderedPolicySources is not on screen at all, so both
  // conditions are defense in depth against a selection reaching the wire
  // without the screen having shown what it grants.
  const selectedTrelloWorkspaceIds =
    mcpContext?.scopeClassification.hasTrello &&
    mcpContext.policies.trello.status === 'success'
      ? mcpContext.selectedTrelloWorkspaces
          .map((workspace) => workspace.id)
          .filter(Boolean)
      : [];
  // Site and Bitbucket read the same visibility flags their selectors render
  // from, so a target is only posted when its picker was on screen.
  const selectedBitbucketWorkspaceIds =
    mcpContext?.showBitbucketSelector &&
    mcpContext.policies.bitbucket.status === 'success'
      ? mcpContext.selectedBitbucketWorkspaces.map((workspace) => workspace.ari)
      : [];
  // Selections from different organizations cannot be granted together, so
  // no target is submitted while they disagree — the same
  // defense in depth as above. Accept is already disabled and the permissions
  // list withdrawn (see mcpAcceptDisabled / McpPermissions), so this only
  // matters if the form is ever submitted by some other path.
  const selectedWorkspaceIds =
    mcpContext?.hasOrgMismatch || mcpContext?.hasSandboxConflict
      ? []
      : [
          ...(mcpContext?.showSiteSelector &&
          mcpContext.policies.site.status === 'success'
            ? parseSelectedSiteUrls(selectedWorkspace)
            : []),
          ...selectedTrelloWorkspaceIds,
          ...selectedBitbucketWorkspaceIds,
        ];

  return (
    <>
      <input
        type="hidden"
        name={MCP_RESUME_FORM_FIELDS.selectedScopes}
        value={selectedScopesJoined}
      />
      <input
        type="hidden"
        name={MCP_RESUME_FORM_FIELDS.selectedToolsets}
        value={selectedToolsetsJoined}
      />
      {selectedWorkspaceIds.map((workspaceId) => (
        <input
          key={workspaceId}
          type="hidden"
          name="selectedWorkspace"
          value={workspaceId}
        />
      ))}
    </>
  );
};

export default McpFlowParameters;
